import { useEffect, useState, type MouseEvent } from 'react';
import { ContextMenuProvider, type ContextMenuItem } from '@realrain/ui/context-menu';
import { setUiLocale, translate, useUiLocale, type UiLocale } from '@realrain/ui/i18n';
import { productUrls } from "./product-urls";
import wonderland from "./art/jiaohui-arrival-bridge.webp";

const urls = productUrls(window.location.href, {
  fair: import.meta.env.VITE_RAINFAIR_URL,
  studio: import.meta.env.VITE_RAINSTUDIO_URL,
  hub: import.meta.env.VITE_RAINHUB_URL,
});
// Public discovery is reachable without signing in.
const entrance = new URL('explore', urls.hub).href;

function Arrow({ diagonal = false }: { diagonal?: boolean }) {
  return (
    <svg aria-hidden="true" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d={diagonal ? "M6 18 18 6M6 6h12v12" : "M5 12h14m-6-6 6 6-6 6"} />
    </svg>
  );
}

// English copy for this page; the shared chrome keeps its own in @realrain/ui/i18n.
const english: Readonly<Record<string, string>> = {
  "发现作品": "Discover works",
  "正在进入…": "Entering…",
  "欢迎来到交汇之地，远道而来的客人。": "Welcome to the Crossroads, traveller from afar.",
  "愿你在此寻得所爱，让眼中的系列，成为身边的风景吧。": "May you find what you love here, and bring the wonderlands you see into the world around you.",
  "云海上的长桥通往交汇之地，远方的系列在灯火间相连。": "A long bridge over a sea of clouds leads to the Crossroads, where distant wonderlands glow and meet.",
};
const japanese: Readonly<Record<string, string>> = {
  "发现作品": "作品を探す",
  "正在进入…": "移動中…",
  "欢迎来到交汇之地，远道而来的客人。": "遠くから来た旅人よ、交差点へようこそ。",
  "愿你在此寻得所爱，让眼中的系列，成为身边的风景吧。": "ここで好きなものに出会い、心に描くワンダーランドを、あなたのそばの風景に。",
  "云海上的长桥通往交汇之地，远方的系列在灯火间相连。": "雲海に架かる長い橋の先には交差点。遠くのワンダーランドが灯りの中でつながっています。",
};
const text = (locale: UiLocale, chinese: string) => (locale === 'ja' ? japanese[chinese] : locale === 'en' ? english[chinese] : chinese) ?? translate(locale, chinese);

export function App() {
  const locale = useUiLocale();
  const menuItems: ContextMenuItem[] = [
    { id: 'platform-crossroads', label: text(locale, '发现作品'), group: 'product', onAction: () => window.location.assign(entrance) },
  ];
  return <ContextMenuProvider getItems={() => menuItems} locale={locale}><PlatformPage locale={locale} /></ContextMenuProvider>;
}
function PlatformPage({ locale }: { locale: UiLocale }) {
  const [entering, setEntering] = useState(false);
  const t = (chinese: string) => text(locale, chinese);
  useEffect(() => {
    document.documentElement.lang = locale === "zh" ? "zh-CN" : locale;
    document.title = locale === 'zh' ? 'RealRain 非雨 · 原创作品与创意周边' : locale === 'ja' ? 'RealRain · オリジナル作品とグッズ' : 'RealRain · Original Art & Creative Goods';
  }, [locale]);
  useEffect(() => {
    const restore = () => setEntering(false);
    window.addEventListener('pageshow', restore);
    return () => window.removeEventListener('pageshow', restore);
  }, []);
  const enter = (event: MouseEvent<HTMLAnchorElement>) => {
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    event.preventDefault();
    if (entering) return;
    setEntering(true);
    window.setTimeout(() => window.location.assign(entrance), window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : 180);
  };
  return (
    <div className={`site-shell${entering ? ' site-shell--entering' : ''}`}>
      <div className="hero-art">
        <img className="hero-world" src={wonderland} alt={t("云海上的长桥通往交汇之地，远方的系列在灯火间相连。")} width="1920" height="1080" fetchPriority="high" />
      </div>
      <header className="site-header">
        <a className="brand" href="/">
          <img className="brand-mark" src="/realrain-logo-rain-celadon-small.svg" alt="" width="28" height="28" />
          <span className="brand-word">RealRain</span>
        </a>
        <select className="language-switch" aria-label={translate(locale, '语言')} value={locale} onChange={event => setUiLocale(event.target.value as UiLocale)}>
          <option value="zh" lang="zh-CN">中文</option><option value="en" lang="en">English</option><option value="ja" lang="ja">日本語</option>
        </select>
      </header>

      <main className="hero-main" aria-labelledby="welcome-title">
        <div className="hero-copy">
          <h1 id="welcome-title"><span>Fantasy World,</span><span>Wonderful Land.</span></h1>
          <div className="hero-intro">
            <p className="hero-description">{t("欢迎来到交汇之地，远道而来的客人。")}</p>
            <p className="hero-wish">{t("愿你在此寻得所爱，让眼中的系列，成为身边的风景吧。")}</p>
          </div>
          <div className="entrance-actions">
            <a className="entrance-action" href={entrance} onClick={enter} aria-busy={entering}><span>{entering ? t('正在进入…') : t('发现作品')}</span><Arrow /></a>
          </div>
        </div>
      </main>
    </div>
  );
}
